import { getUniqueId } from '@/utils/common/uniqueId';
import { ReactNode } from 'react';

type GoogleOauthLoginProps = {
  className?: string;
  redirectUrl?: string;
  isLoginSignupPopup?: boolean;
  onClick?: () => void;
  children: ReactNode;
  /**
   * FTU Popup：落盘当前页 router 上下文，OAuth 回到 auth/google 后用于与 Popup 内 handleGoogleLogin 一致的跳转
   */
  ftuNavContext?: { asPath: string; pathname: string };
};

const GOOGLE_OAUTH_REDIRECT_KEY = 'googleAuthLoginSuccessRedirectUrl';
const GOOGLE_OAUTH_POPUP_KEY = 'googleAuthLoginIsLoginSignupPopup';

/** OAuth 发起前当前页 query，auth/google 与 redirect 合并解析 */
export const GOOGLE_OAUTH_SOURCE_SEARCH_KEY = 'googleOauthSourceSearch';

/** FTU：asPath / pathname / search，供 redirectAppStore / goTo */
export const GOOGLE_OAUTH_FTU_NAV_KEY = 'googleOauthFtuNavContext';

/** 登录成功后写入，用于再次进入 /account/auth/google 时直接 replace，避免重复消费 code */
export const GOOGLE_OAUTH_SUCCESS_GUARD_KEY = 'googleOauthCallbackSuccessGuard';

const GoogleOauthLogin = (props: GoogleOauthLoginProps) => {
  const { className, redirectUrl, isLoginSignupPopup, onClick, children, ftuNavContext } = props;

  const handleClick = () => {
    onClick?.();

    sessionStorage.removeItem(GOOGLE_OAUTH_SUCCESS_GUARD_KEY);

    const redirectUri = `${process.env.NEXT_PUBLIC_WEB_HOST}/account/auth/google`;
    const currentHref = window.location.href;
    const finalRedirectUrl = redirectUrl ?? currentHref;
    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

    if (!clientId) {
      return;
    }

    // 当前 URL 全部 query，auth/google 可与 redirect URL 合并解析
    const search = window.location.search || '';
    sessionStorage.setItem(GOOGLE_OAUTH_SOURCE_SEARCH_KEY, search);

    if (ftuNavContext) {
      sessionStorage.setItem('entryFtu', '1');
      sessionStorage.setItem(
        GOOGLE_OAUTH_FTU_NAV_KEY,
        JSON.stringify({
          asPath: ftuNavContext.asPath,
          pathname: ftuNavContext.pathname,
          search
        })
      );
    } else {
      sessionStorage.removeItem(GOOGLE_OAUTH_FTU_NAV_KEY);
    }

    sessionStorage.setItem(GOOGLE_OAUTH_REDIRECT_KEY, finalRedirectUrl);
    if (isLoginSignupPopup) {
      sessionStorage.setItem(GOOGLE_OAUTH_POPUP_KEY, '1');
    } else {
      sessionStorage.removeItem(GOOGLE_OAUTH_POPUP_KEY);
    }

    const googleAuthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      '&response_type=code&scope=openid%20email%20profile' +
      `&state=${getUniqueId()}`;

    window.location.href = googleAuthUrl;
  };

  return (
    <div
      className={className}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleClick()}>
      {children}
    </div>
  );
};

export default GoogleOauthLogin;
